import * as turf from '@turf/turf'
import { CustomFieldValue } from 'types/assetModel/customField'
import {
  ExternalUserFieldAccess,
  FormField,
  InputType,
} from 'types/geckoFormTypesAndHelpers'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { getDropdownOptionsForEnum } from 'utils/enum'
import {
  parseArray,
  parseDate,
  parseGeoJsonGeometry,
  parseString,
} from 'utils/parsingHelpers'
import { compareAlphanumeric } from 'utils/stringHelpers'
import { optionalStringSchema } from 'utils/zodHelpers'

import { GeckoEntryBase } from './geckoEntry'
import { UserComment } from './geckoUserComment'

export enum OperationalStatus {
  IN_SERVICE = 'In Service',
  OUT_OF_SERVICE = 'Out of Service',
}

export const getUnitFormFields = (unit: GeckoUnit): FormField[] => {
  return [
    {
      dataKey: 'name',
      label: 'Name',
      value: unit.data.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'operationalStatus',
      label: 'Operational Status',
      value: unit.data.operationalStatus,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: getDropdownOptionsForEnum(OperationalStatus),
    },
    {
      dataKey: 'erp_id',
      label: 'ERP ID',
      value: unit.data.erp_id,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'area',
      label: 'Area',
      value: unit.data.area as string,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'area_description',
      label: 'Area Description',
      value: unit.data.area_description,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'description',
      label: 'Description',
      value: unit.data.description,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
  ]
}

interface CurrentAssetBrief {
  conditionSummary?: string
  planSummary?: string
  lowestMinimumRemainingLife: number | undefined
}

/**
 * A physical asset, such as a tank or a boiler. Units are further broken down
 * into formatters which may be individually inspected.
 */
export class GeckoUnit extends GeckoEntryBase<
  {
    name: string
    description: string | undefined
    current_brief: CurrentAssetBrief | undefined
    operationalStatus: OperationalStatus
    built_date: Date | undefined
    retirement_date: Date | undefined
    geography: turf.Geometry | undefined
    area: string | undefined
    area_description: string | undefined
    erp_id: string | undefined
    primary_image: string | undefined
    unitBrief: UserComment | undefined
    custom_fields: CustomFieldValue[] | undefined
    groupings: ReadonlyArray<string> | undefined
    systems: ReadonlyArray<string> | undefined
    comments: Record<string, unknown> | undefined
  },
  {
    organization: string
    site: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    const unitBriefJson = json.data.unitBrief as UserComment | undefined

    const groupingsJSON = json.data.groupings as Array<string> | undefined

    super(
      json,
      {
        name: parseString(json.data.name)!, // name is a required field for units, TODO ARCH-203: remove this "!"
        description: parseString(json.data.description),
        current_brief: json.data.current_brief
          ? (json.data.current_brief as CurrentAssetBrief)
          : undefined,
        operationalStatus:
          (parseString(json.data.operationalStatus) as OperationalStatus) ??
          OperationalStatus.IN_SERVICE,
        built_date: parseDate(json.data.built_date),
        retirement_date: parseDate(json.data.retirement_date),
        geography: parseGeoJsonGeometry(json.data.geography),
        area: parseString(json.data.area),
        area_description: parseString(json.data.area_description),
        erp_id: parseString(json.data.erp_id),
        primary_image: parseString(json.data.primary_image),
        unitBrief: unitBriefJson,
        custom_fields: Array.isArray(json.data.custom_fields)
          ? json.data.custom_fields
          : [],
        groupings: groupingsJSON,
        systems: parseArray(json.data.systems) ?? [],
        comments: json.data.comments as Record<string, unknown> | undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a unit from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoUnit(json)
  }

  /**
   * A simple comparison method, used to sort entries by their names, properly
   * taking into account multi-digit numbers (i.e. 2 will appear before 20)
   * @param a     The first entry
   * @param b     The second entry
   */
  public static DefaultSort(a: GeckoUnit, b: GeckoUnit) {
    return compareAlphanumeric(a.data.name, b.data.name)
  }
}
